import { Frame, PointCloudSpherical, RecordType } from "@app/shared";
import React, { useMemo } from "react";
import { colorWithAlpha } from "../../../../helpers/colorHelpers";
import { ChartBounds, DataChart, DatasetConfig } from "../DataChart";

export type FrameWithOpacity = {
    opacity?: number;
    number?: number;
    frame: Frame;

}
export type RadarChartProps = {
    frames: FrameWithOpacity[]
    bounds?: RadarChartBounds;
    staticPointColor?: string;
    targetColors?: string[];
};
// type RadarPoint = {
//     x: number;
//     y: number;
//     z: number;
//     vx?: number;
//     vy?: number;
//     vz?: number;
//     doppler?: number;
// }

export type RadarChartBounds = {
    minX: number | undefined;
    maxX: number | undefined;
    minY: number | undefined;
    maxY: number | undefined;
}



export const RadarChart: React.FC<RadarChartProps> = (props) => {
    const targetColors = useMemo(() => props.targetColors ?? ["#FF0000"], [props.targetColors]);

    // Create datasets for each frame
    const datasets = useMemo((): DatasetConfig[] => {
        const allDatasets: DatasetConfig[] = [];

        for (let i = 0; i < props.frames.length; i++) {
            const radarFrame = props.frames[i];
            if (!radarFrame || !radarFrame.frame || !radarFrame.frame.tlvs) continue;

            const pointClouds = radarFrame.frame.tlvs.find(tlv => tlv.getDataType() === RecordType.PointCloud)?.pointClouds;
            const staticPoints = radarFrame.frame.tlvs.find(tlv => tlv.getDataType() === RecordType.StaticPointCloud)?.staticPointClouds;
            const targetIndexes = radarFrame.frame.tlvs.find(tlv => tlv.getDataType() === RecordType.RadarTargetIndex)?.targetIndices;

            const pointsByTargetIndex = new Map<number, PointCloudSpherical[]>();
            pointClouds?.forEach((pc, pcIndex) => {
                const targetIndex = targetIndexes?.[pcIndex] ?? 255;
                if (!pointsByTargetIndex.has(targetIndex)) {
                    pointsByTargetIndex.set(targetIndex, new Array<PointCloudSpherical>());
                }
                pointsByTargetIndex.get(targetIndex)?.push(pc);
            });

            const frameAge = (i + 1) - props.frames.length;

            // Add point cloud datasets by target index
            pointsByTargetIndex.forEach((points, targetIndex) => {
                const baseColor = targetIndex === 255
                    ? "#CCCCCC" // Unassociated points color
                    : targetColors[targetIndex % targetColors.length];

                const color = colorWithAlpha(baseColor, radarFrame.opacity ?? 1);

                const data = points.map(pc => ({
                    x: pc.x ?? 0,
                    y: pc.y ?? 0,
                    z: pc.z ?? 0,
                    doppler: pc.doppler
                }));

                allDatasets.push({
                    label: (targetIndex === 255 ? "Unassociated" : "Target " + targetIndex) + (frameAge ? ` (${frameAge})` : ""),
                    data,
                    color,
                    pointRadius: 5
                });
            });

            // Add static points if available
            if (staticPoints) {
                const staticColor = colorWithAlpha(props.staticPointColor ?? "green", radarFrame.opacity ?? 1);

                const data = staticPoints.map(sp => ({
                    x: sp.x ?? 0,
                    y: sp.y ?? 0,
                    z: sp.z ?? 0,
                    doppler: (sp as any).doppler ?? 0  // Static points may not have doppler
                }));

                allDatasets.push({
                    label: "Static" + (frameAge ? ` (${frameAge})` : ""),
                    data,
                    color: staticColor,
                    pointRadius: 5
                });
            }
        }

        return allDatasets;
    }, [props.frames, targetColors, props.staticPointColor]);

    // Convert RadarChartBounds to ChartBounds
    const chartBounds: ChartBounds = {
        minX: props.bounds?.minX,
        maxX: props.bounds?.maxX,
        minY: props.bounds?.minY,
        maxY: props.bounds?.maxY
    };

    const customTooltipCallback = (point: any, datasetLabel: string) => {
        return `(${point.x?.toFixed(3)}, ${point.y?.toFixed(3)}, ${point.z?.toFixed(3)}) Doppler: ${point.doppler?.toFixed(3)}`;
    };

    return (
        <DataChart
            datasets={datasets}
            bounds={chartBounds}
            xAxisLabel="X-Axis"
            yAxisLabel="Y-Axis"
            customTooltipCallback={customTooltipCallback}
            height="500px"
        />
    );
};